import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";

const STATUS_SEVERITY = {
    pending_review: "warning",
    processed:      "success",
    rejected:       "danger",
};

const STATUS_LABEL = {
    pending_review: "Pending Review",
    processed:      "Processed",
    rejected:       "Rejected",
};

// ── Side-by-side Viewer ───────────────────────────────────────────────────────
const UploadViewer = ({ upload, onClose, onMarkProcessed }) => {
    const navigate = useNavigate();

    if (!upload) return null;

    const isPdf = upload.file_type === "pdf";

    const handleCreatePrescription = () => {
        onClose();
        navigate("/prescriptions", {
            state: {
                fromUploadId:               upload.id,
                portalPrescriptionUploadId: upload.id,
                customer:                   upload.customer_pharmacy || null,
            }
        });
    };

    const handleCreateInvoice = () => {
        onClose();
        navigate("/invoices", {
            state: {
                fromUploadId:               upload.id,
                portalPrescriptionUploadId: upload.id,
                customer:                   upload.customer_pharmacy || null,
                openCreate:                 true,
            }
        });
    };

    const hasLinked = (upload.linked_prescriptions?.length > 0) || (upload.linked_invoices?.length > 0);

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.5)", display: "flex",
        }}>
            {/* ── Left: uploaded file ──────────────────────────────────── */}
            <div style={{ width: "50%", background: "#1a1a2e", display: "flex", flexDirection: "column" }}>
                <div style={{
                    padding: "12px 16px", background: "#0f0f1a",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid #333",
                }}>
                    <div>
                        <div style={{ color: "white", fontWeight: 600, fontSize: 14 }}>{upload.original_name}</div>
                        <div style={{ color: "#aaa", fontSize: 12, marginTop: 2 }}>
                            {upload.customer_name}
                            {upload.notes && <span style={{ marginLeft: 8, fontStyle: "italic" }}>· "{upload.notes}"</span>}
                        </div>
                    </div>
                    <a href={upload.file_url} target="_blank" rel="noreferrer"
                        style={{ color: "#60a5fa", fontSize: 12, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="pi pi-external-link" style={{ fontSize: 11 }} /> Open full
                    </a>
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                    {isPdf ? (
                        <iframe src={upload.file_url} title="Prescription" style={{ width: "100%", height: "100%", border: "none" }} />
                    ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto", padding: 16 }}>
                            <img src={upload.file_url} alt="Uploaded prescription" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        </div>
                    )}
                </div>
            </div>

            {/* ── Right: actions panel ──────────────────────────────────── */}
            <div style={{ width: "50%", background: "var(--surface-card)", display: "flex", flexDirection: "column" }}>
                <div style={{
                    padding: "12px 16px", borderBottom: "1px solid var(--border-color)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <span style={{ fontWeight: 600, fontSize: 15, color: "var(--text-primary)" }}>Process Upload</span>
                    <button onClick={onClose} style={{
                        background: "none", border: "1px solid var(--border-color)",
                        borderRadius: 8, width: 32, height: 32, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, color: "var(--text-secondary)",
                    }}>
                        <i className="pi pi-times" />
                    </button>
                </div>

                <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>

                    {/* Customer + status */}
                    <div style={{
                        display: "flex", alignItems: "center", gap: 10, marginBottom: 20,
                        padding: "10px 14px", background: "var(--surface-bg)",
                        borderRadius: 10, border: "1px solid var(--border-color)",
                    }}>
                        <i className="pi pi-user" style={{ color: "var(--text-secondary)", fontSize: 14 }} />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>{upload.customer_name}</div>
                            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 1 }}>
                                {new Date(upload.created_at).toLocaleString()}
                            </div>
                        </div>
                        <Tag
                            value={STATUS_LABEL[upload.status] || upload.status}
                            severity={STATUS_SEVERITY[upload.status] || "info"}
                            style={{ marginLeft: "auto" }}
                        />
                    </div>

                    {/* Patient note */}
                    {upload.notes && (
                        <div style={{
                            background: "var(--brand-light, #eff6ff)", border: "1px solid var(--brand-accent, #bfdbfe)",
                            borderRadius: 8, padding: "10px 14px", fontSize: 13,
                            color: "var(--brand-primary)", marginBottom: 20, display: "flex", gap: 8,
                        }}>
                            <i className="pi pi-comment" style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }} />
                            <span><b>Patient note:</b> {upload.notes}</span>
                        </div>
                    )}

                    {/* ── Linked records ──────────────────────────────── */}
                    {hasLinked && (
                        <div style={{ marginBottom: 20 }}>
                            <div style={{
                                fontSize: 11, fontWeight: 600, color: "var(--text-muted)",
                                textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10,
                            }}>
                                Created from this upload
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {(upload.linked_prescriptions || []).map(p => (
                                    <button key={p.id}
                                        onClick={() => { onClose(); navigate("/prescriptions", { state: { prescriptionId: p.id } }); }}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "10px 14px", borderRadius: 10, width: "100%",
                                            background: "#eff6ff", border: "1px solid #bfdbfe",
                                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                                        }}
                                    >
                                        <i className="pi pi-file" style={{ color: "#1d4ed8", fontSize: 14 }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8" }}>
                                                Prescription #{p.id?.toString().slice(-6)}
                                            </div>
                                            <div style={{ fontSize: 11.5, color: "#3b82f6", marginTop: 1 }}>
                                                {p.date}{p.doctor_name ? ` · Dr. ${p.doctor_name}` : ""}
                                            </div>
                                        </div>
                                        <i className="pi pi-arrow-right" style={{ color: "#93c5fd", fontSize: 12 }} />
                                    </button>
                                ))}
                                {(upload.linked_invoices || []).map(inv => (
                                    <button key={inv.id}
                                        onClick={() => { onClose(); navigate(`/invoices?invoiceId=${inv.id}`); }}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "10px 14px", borderRadius: 10, width: "100%",
                                            background: "#f0fdf4", border: "1px solid #bbf7d0",
                                            cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                                        }}
                                    >
                                        <i className="pi pi-receipt" style={{ color: "#15803d", fontSize: 14 }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#15803d" }}>
                                                Invoice {inv.invoice_number}
                                            </div>
                                            <div style={{ fontSize: 11.5, color: "#16a34a", marginTop: 1 }}>
                                                {inv.date} · {inv.status}
                                            </div>
                                        </div>
                                        <i className="pi pi-arrow-right" style={{ color: "#86efac", fontSize: 12 }} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Action cards ────────────────────────────────── */}
                    <div style={{
                        fontSize: 11, fontWeight: 600, color: "var(--text-muted)",
                        textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12,
                    }}>
                        Choose action
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

                        {/* Option 1: Create prescription */}
                        <div onClick={handleCreatePrescription} style={{
                            border: "1.5px solid var(--border-color)", borderRadius: 12,
                            padding: "16px 18px", cursor: "pointer", transition: "all 0.15s",
                            display: "flex", alignItems: "flex-start", gap: 14,
                        }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = "var(--brand-primary)"}
                            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border-color)"}
                        >
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: "var(--brand-light, #eff6ff)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <i className="pi pi-file-edit" style={{ color: "var(--brand-primary)", fontSize: 17 }} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>Create Prescription</div>
                                <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.5 }}>
                                    Add a new prescription record and bill from it.
                                </div>
                            </div>
                            <i className="pi pi-arrow-right" style={{ marginLeft: "auto", color: "var(--text-muted)", alignSelf: "center", fontSize: 14 }} />
                        </div>

                        {/* Option 2: Create invoice directly */}
                        <div onClick={handleCreateInvoice} style={{
                            border: "1.5px solid var(--border-color)", borderRadius: 12,
                            padding: "16px 18px", cursor: "pointer", transition: "all 0.15s",
                            display: "flex", alignItems: "flex-start", gap: 14,
                        }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = "#059669"}
                            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border-color)"}
                        >
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: "#ecfdf5",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <i className="pi pi-receipt" style={{ color: "#059669", fontSize: 17 }} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>Create Invoice Directly</div>
                                <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.5 }}>
                                    For one-time prescriptions. Skip the prescription record and bill directly.
                                </div>
                            </div>
                            <i className="pi pi-arrow-right" style={{ marginLeft: "auto", color: "var(--text-muted)", alignSelf: "center", fontSize: 14 }} />
                        </div>

                        {/* Mark processed */}
                        {upload.status === "pending_review" && (
                            <button onClick={() => onMarkProcessed(upload.id, "processed")} style={{
                                background: "none", border: "1px solid var(--border-color)",
                                borderRadius: 10, padding: "10px 16px", cursor: "pointer",
                                fontSize: 13, color: "var(--text-secondary)", fontFamily: "inherit",
                                display: "flex", alignItems: "center", gap: 7, justifyContent: "center",
                            }}>
                                <i className="pi pi-check" style={{ fontSize: 12 }} />
                                Mark as processed (no further action)
                            </button>
                        )}

                        {/* Reject */}
                        {upload.status === "pending_review" && (
                            <button onClick={() => onMarkProcessed(upload.id, "rejected")} style={{
                                background: "none", border: "1px solid #fca5a5",
                                borderRadius: 10, padding: "10px 16px", cursor: "pointer",
                                fontSize: 13, color: "#dc2626", fontFamily: "inherit",
                                display: "flex", alignItems: "center", gap: 7, justifyContent: "center",
                            }}>
                                <i className="pi pi-times" style={{ fontSize: 12 }} />
                                Reject this upload
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const PrescriptionUploads = () => {
    const [uploads,  setUploads]  = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [loading,  setLoading]  = useState(true);
    const [viewing,  setViewing]  = useState(null);
    const location  = useLocation();
    const navigate  = useNavigate();

    useEffect(() => { fetchUploads(); }, [queryParams]);

    // Auto-open if navigated from notification with ?id= — fetched directly by id
    // rather than searched for in `uploads`, since that only holds the current page.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");
        if (!id) return;
        axiosInstance.get(`/api/prescription-uploads/${id}/`)
            .then(res => setViewing(res.data))
            .catch(handleApiError);
    }, [location.search]);

    const fetchUploads = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/api/prescription-uploads/", { params: queryParams });
            setUploads(res.data.results);
            setTotalRecords(res.data.count);
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await axiosInstance.patch(`/api/prescription-uploads/${id}/`, { status, pharmacy_notes: "" });
            setUploads(prev => prev.map(u => u.id === id ? { ...u, status } : u));
            setViewing(null);
            handleApiSuccess(`Upload marked as ${status}`);
        } catch (err) {
            handleApiError(err);
        }
    };

    // ── Column templates ──────────────────────────────────────────────────────

    const fileTemplate = (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className={`pi ${row.file_type === "pdf" ? "pi-file-pdf" : "pi-image"}`}
                style={{ color: row.file_type === "pdf" ? "#dc2626" : "#2563eb", fontSize: 16 }} />
            <span style={{ fontSize: 13 }}>{row.original_name}</span>
        </div>
    );

    const notesTemplate = (row) => (
        row.notes
            ? <span style={{ fontSize: 12, fontStyle: "italic", color: "var(--text-secondary)" }}>"{row.notes}"</span>
            : <span style={{ color: "var(--text-muted)" }}>—</span>
    );

    const statusTemplate = (row) => (
        <Tag
            value={STATUS_LABEL[row.status] || row.status}
            severity={STATUS_SEVERITY[row.status] || "info"}
        />
    );

    const linkedTemplate = (row) => {
        const prescriptions = row.linked_prescriptions || [];
        const invoices      = row.linked_invoices      || [];
        if (!prescriptions.length && !invoices.length) {
            return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
        }
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {prescriptions.map(p => (
                    <button key={p.id}
                        onClick={(e) => { e.stopPropagation(); navigate("/prescriptions", { state: { prescriptionId: p.id } }); }}
                        style={{
                            background: "#eff6ff", border: "1px solid #bfdbfe",
                            borderRadius: 6, padding: "2px 8px", fontSize: 11.5,
                            color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit",
                            display: "flex", alignItems: "center", gap: 4,
                        }}
                    >
                        <i className="pi pi-file" style={{ fontSize: 10 }} />
                        Rx #{p.id?.toString().slice(-6)}
                        <i className="pi pi-arrow-right" style={{ fontSize: 9 }} />
                    </button>
                ))}
                {invoices.map(inv => (
                    <button key={inv.id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/invoices?invoiceId=${inv.id}`); }}
                        style={{
                            background: "#f0fdf4", border: "1px solid #bbf7d0",
                            borderRadius: 6, padding: "2px 8px", fontSize: 11.5,
                            color: "#15803d", cursor: "pointer", fontFamily: "inherit",
                            display: "flex", alignItems: "center", gap: 4,
                        }}
                    >
                        <i className="pi pi-receipt" style={{ fontSize: 10 }} />
                        {inv.invoice_number}
                        <i className="pi pi-arrow-right" style={{ fontSize: 9 }} />
                    </button>
                ))}
            </div>
        );
    };

    const actionTemplate = (row) => (
        <Button
            icon="pi pi-eye"
            label="View & Process"
            className="p-button-sm p-button-outlined"
            onClick={() => setViewing(row)}
        />
    );

    const columns = [
        { accessor: "customer_name", Header: "Patient"                                          },
        { accessor: "file",          Header: "File",            bodyTemplate: fileTemplate      },
        { accessor: "created_at",    Header: "Uploaded",        dataType: "date"                },
        { accessor: "notes",         Header: "Patient Notes",   bodyTemplate: notesTemplate     },
        { accessor: "status",        Header: "Status",          bodyTemplate: statusTemplate,   filtered: true },
        { accessor: "linked",        Header: "Linked Records",  bodyTemplate: linkedTemplate    },
        { accessor: "id",            Header: "Actions",         bodyTemplate: actionTemplate    },
    ];

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    data={uploads}
                    columns={columns}
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                    tabletitle="Prescription Uploads"
                    loading={loading}
                    showEditColumn={false}
                />

                {viewing && (
                    <UploadViewer
                        upload={viewing}
                        onClose={() => setViewing(null)}
                        onMarkProcessed={updateStatus}
                    />
                )}
            </div>
        </div>
    );
};

export default PrescriptionUploads;